import {HttpHeaders} from '@angular/common/http';
import {ChangeDetectorRef, Component, effect, inject, OnDestroy, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {MatIconRegistry} from '@angular/material/icon';
import {DomSanitizer} from '@angular/platform-browser';
import {NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet} from '@angular/router';
import {NGXLogger, NgxLoggerLevel} from 'ngx-logger';
import {of, Subscription} from 'rxjs';
import {filter, switchMap} from 'rxjs/operators';
import {environment} from '../environments/environment';
import * as v from '../version';
import {AboutDialogComponent} from './about.component';
import {AuthenticationService} from './authentication/authentication.service';
import {FeedbackDialogComponent} from './comps/feedback/feedback.component';
import {LocalStorageKeys} from './models/server/Constants';
import {Organisation} from './models/server/Organisation';
import {Roles, User} from './models/server/User';
import {ErrorHandlerService} from './services/errorhandler.service';
import {HelpService} from './services/help.service';
import {LimitsService} from './services/limits.service';
import {OrganisationService} from './services/organisation.service';
import { ReadOnlyBannerComponent } from './comps/read-only-banner/read-only-banner.component';
import {UserService} from './services/user.service';
import {UserProfileDialogComponent} from './settings/dialogs/userprofiledialog/userprofiledialog.component';
import {LoadingService, LoadingState} from "./services/loading.service";
import {MessageService} from "./services/message.service";
import {EventHubComponent} from './comps/event-hub/event-hub.component';
import {HelpPanelComponent} from './comps/help-panel/help-panel.component';
import {NotificationsComponent} from './comps/notifications/notifications.component';
import {ErrorHandlerComponent} from './errorhandler.component';
import {PlanExpiryComponent} from './comps/plan-expiry/plan-expiry.component';
import {UpdateNotificationComponent} from './comps/update-notification/update-notification.component';
import {LimitAlertComponent} from './comps/limit-alert/limit-alert.component';
import {InvitecheckComponent} from './comps/invitecheck/invitecheck.component';
import {LoginButtonsComponent} from './authentication/login-buttons/login-buttons.component';
import {SimpleProgressComponent} from './comps/simpleprogress.component';
import {OrgNotDeactivatedDirective} from './services/activeOrg.directive';
import {AuthorisationDirective} from './authentication/authorisation.directive';
import {MaterialModule} from './material.module';
import {LocalStoreService} from "./services/local-store.service";

@Component({
  selector: 'app-root',
  styleUrls: ['./app.component.scss'],
  templateUrl: './app.component.html',
  imports: [
    MaterialModule,
    RouterLink,
    AuthorisationDirective,
    RouterLinkActive,
    OrgNotDeactivatedDirective,
    SimpleProgressComponent,
    LoginButtonsComponent,
    InvitecheckComponent,
    LimitAlertComponent,
    UpdateNotificationComponent,
    PlanExpiryComponent,
    ReadOnlyBannerComponent,
    RouterOutlet,
    ErrorHandlerComponent,
    NotificationsComponent,
    HelpPanelComponent,
    EventHubComponent
  ]
})
export class AppComponent
  implements OnInit, OnDestroy {

  // injected services
  private localStore = inject(LocalStoreService);

  environment = environment;

  versionString: string;
  organisation: Organisation | null = null;
  userRecord: User | null = null;
  consoleOnly = false;

  // Gates the post-login redirect so it fires exactly once per authenticated session,
  // independent of whether the fetched Organisation happens to be reference-equal to what's
  // already cached (e.g. re-authenticating in the same tab without a reload, or
  // SignInComponent's own cache-warming beating this to the first getOrganisation() call) -
  // #1767: reference-equality on `organisation` alone was silently skipping the redirect.
  private hasRedirectedAfterLogin = false;
  busy = false;
  busyMessage = '';
  loading = true;

  // private limitTimer: Observable<number>;
  private limitTimerSub?: Subscription;
  private loadingSub?: Subscription;
  orgUpdatedSub?: Subscription;

  // get canShowSettings(): boolean {
  //   return this.userRecord && (this.userRecord.role === Roles.Subscriber || this.userRecord.role === Roles.Admin);
  // }

  constructor(
    public authService: AuthenticationService,
    private userService: UserService,
    private organisationService: OrganisationService,
    private dialog: MatDialog,
    private domSanitizer: DomSanitizer,
    private matIconRegistry: MatIconRegistry,
    private helpService: HelpService,
    private errHandler: ErrorHandlerService,
    private logger: NGXLogger,
    private router: Router,
    private loadingService: LoadingService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef,
    limitService: LimitsService) {

    // globally prevent context menu
    if (environment.production) {
      window.addEventListener("contextmenu", e => e.preventDefault());
    }

    router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      let url = event.urlAfterRedirects;
      if (url.indexOf('?')) {
        url = url.substring(0, url.indexOf('?'));
      }
    });

    this.loadingSub = this.loadingService.change
      .pipe(
        filter(state => state.owner === 'app')
      )
      .subscribe((val: LoadingState) => {
        if (val.owner) {
          this.busy = val.isLoading;
          this.busyMessage = val.message;
          this.cdr.markForCheck();
        }
      });

    this.versionString = v.version.version;
    this.loadIcons();

    effect((onCleanup) => {
      const authed = this.authService.isAuthenticated();
      let authFlowSub: Subscription | undefined;

      if (!authed) {
        this.logger.updateConfig({
          level: NgxLoggerLevel.INFO,
          customHttpHeaders: new HttpHeaders({Authorization: `Anonymous`})
        });
        this.userRecord = null;
        this.consoleOnly = false;
        this.organisation = null;
        this.hasRedirectedAfterLogin = false;
        this.loading = false;
        this.cdr.markForCheck();
        return;
      }

      authFlowSub = this.authService.getToken().pipe(
        switchMap(token => {
          if (token) {
            this.logger.updateConfig({
              level: NgxLoggerLevel.INFO,
              customHttpHeaders: new HttpHeaders({Authorization: `Bearer ${token}`})
            });
          } else {
            this.logger.updateConfig({
              level: NgxLoggerLevel.INFO,
              customHttpHeaders: new HttpHeaders({Authorization: `Anonymous`})
            });
          }

          return this.userService.getUser();
        }),
        switchMap((user: User | null) => {
          this.userRecord = user;
          if (user) {

            this.consoleOnly = user.role === Roles.Operator;

            return this.organisationService.getOrganisation();
          } else {
            return of(null);
          }
        })
      ).subscribe({
        next: org => {
          if (this.organisation != org) {
            this.organisation = org;

            if (this.organisation) {
              this.messageService.connect(environment.msgApiUrl, this.userRecord!.id, this.userRecord!.organisationID); // 'ws://localhost:8080'
            }
          }

          // Gated on hasRedirectedAfterLogin rather than folded into the reference-equality
          // check above - getOrganisation() is cached, so re-authenticating in the same tab
          // (or SignInComponent's own cache-warming call landing first) can make `org` come
          // back reference-equal to what's already stored, which used to skip this whole
          // block and leave the user stranded on the sign-in page - #1767.
          if (this.organisation && !this.hasRedirectedAfterLogin) {
            this.hasRedirectedAfterLogin = true;

            if (!this.organisation.isActivated) {
              // org not activated - must do this first
              //this.showOrganisationSettings(this.userRecord?.isSubscriber);
              this.router.navigateByUrl('/settings/organisation');
            } else if (this.localStore.getItem(LocalStorageKeys.ReopenAddOn)) {
              // returning after attaching addon
              this.router.navigateByUrl('/settings/addons');

            } else {
              // redirect to the last open page only if this is the first hit
              const showHome = this.localStore.getItem(LocalStorageKeys.ShowHomeAtStart) === 'true' || this.organisation.isTrialExpired || this.organisation.isDeactivated;
              const currentPath = window.location.pathname;
              // sign-in completes without ever navigating away from /signin (and the other
              // pre-auth routes), so those must count as "first hit" the same as a fresh '/'
              // load, or a stored LastPage from an earlier session leaves the user stranded
              // on the sign-in page after successfully authenticating - #1767
              const preAuthRoutes = ['/', '/signin', '/signup', '/confirm', '/resetpass', '/changepass'];
              const shouldRestoreLastPage = !currentPath || preAuthRoutes.includes(currentPath);

              if (!showHome) {
                const lp = this.localStore.getItem(LocalStorageKeys.LastPage);

                if (!lp) {
                  if (this.userRecord?.role === Roles.Operator) {
                    this.router.navigateByUrl('/console');
                  } else {
                    this.router.navigateByUrl('/design');
                  }
                } else if (shouldRestoreLastPage && !currentPath.startsWith(lp)) {
                  this.router.navigateByUrl(lp);
                }
              } else if (shouldRestoreLastPage) {
                // #1902: unconditionally navigating here bounced a user who'd deep-linked
                // straight to a specific page (e.g. /design) back to '/' just because their
                // showHome preference/org state was set - only force them to '/' on the same
                // "first hit from a pre-auth route" condition the !showHome branch above uses.
                this.router.navigateByUrl('/');
              }
            }
          }

          this.loading = false;
          this.cdr.markForCheck();
        },
        error: async err => {
          this.loading = false;
          this.cdr.markForCheck();
          await this.errHandler.handleErr(err);
        }
      });

      onCleanup(() => authFlowSub?.unsubscribe());
    });

    /* TURN OFF LIMIT CHECK

    let firstTime = true;
    this.limitTimer = timer(5 * 1000, 10 * 1000 * 60 * 12);  // 5 seconds, then every 12 hours
    this.limitTimerSub = this.limitTimer.pipe(
      switchMap(x => this.authService.isAuthenticated),
      switchMap(auth => {
        if (auth) {
          return limitService.checkResourceLimit(null, false); // use firsTime to show at startup
        } else {
          return of(null);
        }
      })
    ).subscribe(() => {
      firstTime = false;
    });
    */
  }

  ngOnInit(): void {

    this.orgUpdatedSub = this.organisationService.orgUpdate.subscribe((org: Organisation) => {
      if (org) {
        this.organisation = org;
        this.cdr.markForCheck();
      }
    });

  }

  goto(anchor: string): void {
    window.location.href = anchor;
  }

  ngOnDestroy(): void {

    this.limitTimerSub?.unsubscribe();
    this.limitTimerSub = undefined;

    this.loadingSub?.unsubscribe();
    this.loadingSub = undefined;
  }

  about() {
    this.dialog.open(AboutDialogComponent);
  }

  feedback(): void {
    this.dialog.open(FeedbackDialogComponent);
  }

  showUserProfile() {
    this.dialog.open(UserProfileDialogComponent);
  }

  // showOrganisationSettings(userIsSubscriber?: boolean) {
  //   if (!this.consoleOnly) {
  //     this.dialog.open(SettingsDialogComponent,
  //       {
  //         data: userIsSubscriber || false,
  //         height: '750px'
  //       });
  //   }
  // }

  // showOrganisationFonts(userIsSubscriber?: boolean) {
  //   if (!this.consoleOnly) {
  //     this.dialog.open(FontSettingsDialogComponent,
  //       {
  //         data: !!userIsSubscriber,
  //         // height: '750px'
  //       });
  //   }
  // }
  //
  // showPrinters(): void {
  //   this.dialog.open(ManagePrintersDialogComponent,
  //     {
  //       // height: '560px',
  //       width: '900px'
  //     });
  // }
  //
  // showSerials(): void {
  //   this.dialog.open(SerialNumberListDialogComponent);
  // }
  //
  // showVariables(): void {
  //
  //   this.dialog.open(VariablesDialogComponent, {
  //     data: {
  //       labelView: null,
  //       selectMode: false,
  //       allowClear: false,
  //       selected: null,
  //       editGlobal: true
  //     }
  //   })
  // }
  //
  // showAddons(): void {
  //   this.dialog.open(ManageAddOnsDialogComponent);
  // }


  showHelpPage(pageId: string): void {
    this.helpService.showPage(pageId);
  }

  private loadIcons(): void {
    // custom icons
    this.matIconRegistry.addSvgIcon(
      'xero',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/addOns/xero/xeroicon.svg')
    );
    // this.matIconRegistry.addSvgIcon(
    //   'logout',
    //   this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/logout.svg')
    // );
    // this.matIconRegistry.addSvgIcon(
    //   'login',
    //   this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/login.svg')
    // );

    this.matIconRegistry.setDefaultFontSetClass('material-icons-outlined');
    //this.matIconRegistry.setDefaultFontSetClass('material-icons-round');
    //this.matIconRegistry.setDefaultFontSetClass('material-icons-sharp');
    // this.matIconRegistry.setDefaultFontSetClass('material-icons-two-tone');

    this.matIconRegistry.addSvgIcon(
      'revert',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/revert.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'group',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/group.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'ungroup',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/ungroup.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'horizontal_align_left',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/horizontal_align_left-black-18dp.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'horizontal_align_center',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/horizontal_align_center-black-18dp.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'horizontal_align_right',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/horizontal_align_right-black-18dp.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'grid-snap',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/grid-snap.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'grid-size',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/grid-size.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'console',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/console.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'rotate_0',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/designer/rotate_0.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'rotate_90',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/designer/rotate_90.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'rotate_180',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/designer/rotate_180.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'rotate_270',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/designer/rotate_270.svg')
    );

  }
}
